import { useEffect, useState } from "react";
import { useLang } from "../i18n/LanguageContext";
import { renderRich } from "../lib/rich.jsx";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEFAULT_PORTRAIT =
  "https://cdn.prod.website-files.com/67ebf279c44c5fad2f90f315/67ebf279c44c5fad2f90f34b_Tico%20Moraes.webp";

function resolveUrl(url) {
  if (!url) return "";
  if (url.startsWith("http") || url.startsWith("data:")) return url;
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
}

export default function About() {
  const { t } = useLang();
  const [portrait, setPortrait] = useState(DEFAULT_PORTRAIT);

  useEffect(() => {
    fetch(`${API}/settings`)
      .then((r) => r.json())
      .then((d) => {
        if (d?.about_portrait_url) setPortrait(resolveUrl(d.about_portrait_url));
      })
      .catch(() => {});
  }, []);

  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-24 md:py-40 border-b border-[#1a1a1a]"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-5">
          <div className="relative w-full aspect-[3/4] overflow-hidden bg-[#121212]">
            <img
              src={portrait}
              alt="Tico Moraes — Creative Consultant"
              data-testid="about-portrait"
              className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-700"
              loading="lazy"
            />
            <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.2em] text-white/70">
              <span>Tico Moraes</span>
              <span className="text-[#ff4500]">2026</span>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7 lg:pl-10">
          <div className="flex items-center gap-4 mb-10">
            <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#ff4500]">
              {t.about.label}
            </span>
            <span className="h-[1px] w-16 bg-[#262626]" />
          </div>

          <h2
            data-testid="about-heading"
            className="font-serif text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight mb-10"
          >
            {t.about.headingA}{" "}
            <span className="italic text-[#ff4500]">{t.about.headingB}</span> {t.about.headingC}{" "}
            <span className="italic">{t.about.headingD}</span>.
          </h2>

          <div className="space-y-6 text-neutral-400 text-base md:text-lg leading-relaxed max-w-2xl">
            <p data-testid="about-p1">{renderRich(t.about.p1)}</p>
            <p data-testid="about-p2">{renderRich(t.about.p2)}</p>
            <p data-testid="about-p3">{renderRich(t.about.p3)}</p>
            {t.about.p4 && <p data-testid="about-p4">{renderRich(t.about.p4)}</p>}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#1a1a1a] mt-14">
            {t.about.stats.map((s, i) => (
              <div
                key={i}
                data-testid={`about-stat-${i}`}
                className="bg-[#0a0a0a] p-6 md:p-8"
              >
                <div className="font-serif text-4xl md:text-5xl text-[#ff4500] font-light">
                  {s.value}
                </div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 mt-3">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
